import { isPlatformBrowser } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { Observable, ReplaySubject, Subscriber, Subscription, of } from 'rxjs';
import { catchError, skip, take, tap } from 'rxjs/operators';
import { onWindowFocus } from '../lib/on-window-focus';

@Injectable({
  providedIn: 'root',
})
export class ObservableDataLoaderService {
  private subjects: Map<string, ReplaySubject<unknown>> = new Map();
  private observables: Map<string, Observable<unknown>> = new Map();
  private maxAgeTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private subsriberCounts: Map<string, number> = new Map();
  private loadFunctions: Map<string, () => Observable<unknown>> = new Map();
  private maxAge: Map<string, { maxAge: number; refreshOnMaxAge: boolean }> = new Map();

  private platformId = inject(PLATFORM_ID);
  private zone = inject(NgZone);

  /**
   * Retrieves the data either from the cache (observable) or if not found,
   * executes the function and updates the ReplaySubject. The timeout for
   * refreshing the data is cleared when there are no subscribers to the observable.
   *
   * @param key - Identifies the data in the cache
   * @param fn - Function to load the data
   * @param maxAge - Optional parameter to specify the maximum age of the cached data
   *                 before it is refreshed
   * @returns - The loaded data as an Observable
   */
  get<T>(key: string, fn: () => Observable<T>, maxAge?: number, refreshOnMaxAge = true): Observable<T> {
    let subject: ReplaySubject<T>;
    let observable: Observable<T>;
    if (maxAge) this.maxAge.set(key, { maxAge, refreshOnMaxAge });
    if (!this.subjects.has(key)) {
      subject = new ReplaySubject<T>(1);
      this.subjects.set(key, subject);
      this.loadFunctions.set(key, fn);
      this.loadData(key);
      observable = this.handleSubscriberCountChange(subject, key);
      this.observables.set(key, observable);
    } else {
      subject = this.subjects.get(key) as ReplaySubject<T>;
      observable = this.observables.get(key) as Observable<T>;
    }
    return observable;
  }

  private loadData<T>(key: string) {
    const fn = this.loadFunctions.get(key) as () => Observable<T>;
    if (!fn) return;

    const subject = (this.subjects.get(key) as ReplaySubject<T>) || new ReplaySubject<T>(1);

    return fn()
      .pipe(
        take(1),
        tap(data => {
          if (data !== undefined && data !== null) {
            subject.next(data);
          } else {
            this.subjects.delete(key);
            subject.next(data);
          }
        }),
        catchError(err => {
          this.subjects.delete(key);
          subject.error(err);
          return of(undefined);
        })
      )
      .subscribe(() => {
        this.addTimeout(key);
      });
  }

  handleSubscriberCountChange<T>(sourceObservable: Observable<T>, key: string) {
    return new Observable((subscriber: Subscriber<T>) => {
      const counter = this.subsriberCounts.get(key) || 0;
      this.subsriberCounts.set(key, counter + 1);
      sourceObservable.subscribe(subscriber);
      return () => {
        const counter = this.subsriberCounts.get(key) || 1;
        this.subsriberCounts.set(key, counter - 1);
      };
    });
  }

  addTimeout<T>(key: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const maxAgeSettings = this.maxAge.get(key);
    if (!maxAgeSettings) return;

    const fn = this.loadFunctions.get(key) as () => Observable<T>; // Added this line to get the load function
    if (!fn) return;

    const { maxAge, refreshOnMaxAge } = maxAgeSettings;

    if (this.maxAgeTimeouts.has(key)) clearTimeout(this.maxAgeTimeouts.get(key));
    this.maxAgeTimeouts.set(
      key,
      this.zone.runOutsideAngular(() =>
        setTimeout(() => {
          this.maxAgeTimeouts.delete(key);
          if (refreshOnMaxAge && this.subsriberCounts.get(key)) {
            onWindowFocus().then(() => {
              this.zone.runGuarded(() => {
                this.loadData(key);
              });
            });
          } else {
            this.remove(key);
          }
        }, maxAge)
      )
    );
  }

  getFromCache<T>(key: string): Observable<T> {
    return (this.subjects.get(key) as Observable<T>) || of(null);
  }

  getFromCacheFuzzy<T>(key: string): { key: string; observable: Observable<T> }[] {
    const keys = Array.from(this.subjects.keys()).filter(_key => _key.includes(key));
    return keys.map(_key => ({
      observable: this.subjects.get(_key) as Observable<T>,
      key: _key,
    }));
  }

  private set<T>(key: string, value: T): void {
    const subject = this.subjects.get(key) || new ReplaySubject<T>(1);
    this.subjects.set(key, subject);
    subject.next(value);
  }

  update<T>(key: string, fun: (arg: T, key: string) => T): Subscription {
    const subject = this.subjects.get(key) as ReplaySubject<T>;
    if (!subject) return this.loadData(key);
    return subject.pipe(take(1)).subscribe(value => {
      const updatedValue = fun(value, key);
      if (updatedValue !== undefined) this.set(key, updatedValue);
    });
  }

  updateFuzzy<T>(key: string, fun: (arg: T, key: string) => T): void {
    const keys = Array.from(this.subjects.keys()).filter(_key => _key.includes(key));
    keys.forEach(_key => this.update(_key, fun));
  }

  /**
   * Invalidates data in the cache by setting its value to null
   * @param key string
   */
  remove(key: string): void {
    this.subjects.delete(key);
  }

  /**
   * Invalidates any data in the cache where the key contains the key passed in
   * @param key string used for fuzzy matching
   */
  removeFuzzy(key: string): void {
    const keys = Array.from(this.subjects.keys()).filter(_key => _key.includes(key));
    keys.forEach(_key => this.remove(_key));
  }

  forceDataRefresh<T>(key: string): Observable<T> {
    this.loadData(key);
    const observable = this.observables.get(key);
    if (!observable) return of(undefined);
    // skipping to avoid returning the cached value
    return observable.pipe(skip(1)) as Observable<T>;
  }

  forceDataRefreshFuzzy(key: string): void {
    const keys = Array.from(this.subjects.keys()).filter(_key => _key.includes(key));
    keys.forEach(_key => this.forceDataRefresh(_key));
  }

  /**
   * Clears the cache
   */
  clear(): void {
    this.subjects.clear();
    this.observables.clear();
    this.maxAgeTimeouts.forEach(timeout => clearTimeout(timeout));
    this.maxAgeTimeouts.clear();
  }
}
